import { gql, useMutation } from '@apollo/client';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormErrorMessage,
  Switch,
} from '@apollo/orbit';
import React from 'react';
import { useLocation } from 'react-router';

import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { scrollTo } from 'src/lib/scrollTo';

export const BetaFeaturesCardUserFragment = gql`
  fragment BetaFeaturesCardUserFragment on User {
    id
    betaFeaturesOn
  }
`;

const ChangeBetaFeaturesOnMutation = gql`
  mutation UI__BetaFeaturesCardChangeBetaFeaturesOnMutation(
    $userId: ID!
    $newSetting: Boolean!
  ) {
    user(id: $userId) {
      updateBetaFeaturesOn(betaFeaturesOn: $newSetting) {
        id
        betaFeaturesOn
      }
    }
  }
`;

interface Props {
  user: GraphQLTypes.BetaFeaturesCardUserFragment;
}

export function BetaFeaturesCard({ user: { id, betaFeaturesOn } }: Props) {
  const [changeBetaFeaturesOn, { error }] = useMutation<
    GraphQLTypes.UI__BetaFeaturesCardChangeBetaFeaturesOnMutation,
    GraphQLTypes.UI__BetaFeaturesCardChangeBetaFeaturesOnMutationVariables
  >(ChangeBetaFeaturesOnMutation);

  const labelledBySetting = useAriaDescribedBySetting(
    'betafeatures-setting-label-',
  );
  const describedBySetting = useAriaDescribedBySetting(
    'betafeatures-setting-description-',
  );

  const location = useLocation();

  React.useEffect(() => {
    if (location.hash.length) scrollTo(location.hash);
  }, [location.hash]);

  return (
    <Card id="preview-features">
      <CardHeader>
        <CardTitle>Preview features</CardTitle>
        <CardDescription>
          Enabling Preview Features allows you to use Studio features that are
          still in development. Your experience may be different from your
          teammates if they have not also enabled Preview Features. We may
          contact you via your account email to ask about your experience using
          some features. These features are likely to change based on feedback,
          and may not be released to General Availability the same way you used
          it during the preview period, or at all.
        </CardDescription>

        <FormControl isInvalid={!!error} className="!flex-col !items-end gap-2">
          <Switch
            aria-labelledby={labelledBySetting}
            aria-describedby={describedBySetting}
            isChecked={!!betaFeaturesOn}
            onChange={() => {
              changeBetaFeaturesOn({
                variables: { userId: id, newSetting: !betaFeaturesOn },
              });
            }}
          />
          {error && (
            <FormErrorMessage>
              {error.name}: {error.message}
            </FormErrorMessage>
          )}
        </FormControl>
      </CardHeader>
    </Card>
  );
}
